import { hasMinRole, type SystemRole } from "./constants";

/**
 * VIB-2726: «hvem har tilgang til denne mappa», regnet ut i frontend.
 *
 * Dette er en FORKLARING, ikke en håndhevelse. Tilgangen avgjøres av
 * `can_read_document_folder` / `can_write_document_folder` i databasen, og
 * reglene her speiler dem bevisst linje for linje:
 *
 *   per mappe:      rang-gulvet ELLER en gruppetildeling
 *   over kjeden:    OG — du må passere i mappa OG i hver mappe over den
 *   skriving:       krever at du kan lese hele kjeden, i tillegg til
 *                   skrive-gulvet eller en skrive-tildeling per mappe
 *   slettet mappe:  blokkerer hele kjeden
 *
 * Divergerer disse to, er det SQL-en som har rett. Derfor ligger regelen i én
 * ren funksjon med tester som er skrevet for å feile hvis den begynner å gi
 * tilgang SQL-en ikke ville gitt — det er den retningen som er farlig.
 */

/** Mappe slik utregningen trenger den. Delmengde av `DBDocumentFolder`. */
export interface FolderNode {
  id: string;
  name: string;
  parent_id: string | null;
  read_min_role: string;
  write_min_role: string;
  deleted_at?: string | null;
}

export interface FolderGrantRow {
  folder_id: string;
  group_id: string;
  can_read: boolean;
  can_write: boolean;
}

export interface GroupWithMembers {
  id: string;
  name: string;
  member_ids: string[];
}

export interface PersonRow {
  id: string;
  full_name: string;
  role: string;
}

/** Én grunn til at en person har tilgang via en gruppe. */
export interface AccessGrantSource {
  group_id: string;
  group_name: string;
  folder_id: string;
  folder_name: string;
  /** Tildelingen gir skrive, ikke bare lese. */
  can_write: boolean;
  /** Tildelingen ligger på en mappe OVER den vi ser på. */
  inherited: boolean;
}

export interface FolderAccessPerson {
  user_id: string;
  full_name: string;
  role: string;
  can_read: boolean;
  can_write: boolean;
  /** Rangen alene holder hele veien — personen trenger ingen gruppe. */
  via_rank: boolean;
  /** Gruppetildelingene som gjelder denne personen i kjeden. */
  grants: AccessGrantSource[];
  /**
   * Personen leser mappa, men bare fordi en gruppe gir det. Mister gruppa
   * tildelingen, mister personen tilgangen.
   */
  depends_on_group: boolean;
}

/** Samme dybdegrense som `document_folder_chain` i databasen. */
const MAX_DEPTH = 32;

/**
 * Mappa selv og alle mapper over den, nærmeste først.
 *
 * Dybdetelleren og `sett` er ikke pynt: en FK til egen tabell hindrer ikke en
 * syklus, og en mappe som mangler i lista (fordi RLS skjulte forelderen)
 * stopper kjeden i stedet for å løpe videre.
 */
export function folderChain(
  folderId: string,
  folders: readonly FolderNode[],
): FolderNode[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const kjede: FolderNode[] = [];
  const sett = new Set<string>();
  let current: string | null = folderId;

  while (current && kjede.length < MAX_DEPTH) {
    if (sett.has(current)) break;
    sett.add(current);
    const node = byId.get(current);
    if (!node) break;
    kjede.push(node);
    current = node.parent_id;
  }
  return kjede;
}

/**
 * Om kjeden kan leses av forelderen vi ikke fikk se.
 *
 * Mangler en forelder i `folders`, er den enten slettet eller skjult av RLS.
 * Da vet vi ikke nok til å si at kjeden går gjennom, og svaret er «ukjent» —
 * som lista viser som en advarsel i stedet for å gjette.
 */
export function chainIsComplete(
  folderId: string,
  folders: readonly FolderNode[],
): boolean {
  const kjede = folderChain(folderId, folders);
  const siste = kjede[kjede.length - 1];
  return !!siste && siste.parent_id === null;
}

function grantsForUser(
  userId: string,
  kjede: readonly FolderNode[],
  grants: readonly FolderGrantRow[],
  groups: readonly GroupWithMembers[],
  targetId: string,
): AccessGrantSource[] {
  const mine = new Set(
    groups.filter((g) => g.member_ids.includes(userId)).map((g) => g.id),
  );
  if (mine.size === 0) return [];
  const navn = new Map(groups.map((g) => [g.id, g.name]));
  const treff: AccessGrantSource[] = [];

  for (const node of kjede) {
    for (const g of grants) {
      if (g.folder_id !== node.id || !mine.has(g.group_id)) continue;
      if (!g.can_read && !g.can_write) continue; // DB-CHECK-en skal hindre det
      treff.push({
        group_id: g.group_id,
        group_name: navn.get(g.group_id) ?? "Ukjent gruppe",
        folder_id: node.id,
        folder_name: node.name,
        can_write: g.can_write,
        inherited: node.id !== targetId,
      });
    }
  }
  return treff;
}

/**
 * Hvem har tilgang til `folderId`, og hvorfor.
 *
 * Returnerer også personer som IKKE har tilgang men er med i en gruppe med en
 * tildeling i kjeden. Det er med vilje: en tildeling på en undermappe som
 * blokkeres av en strengere forelder ser ut som tilgang i en liste som bare
 * teller tildelinger, og det er nettopp den misforståelsen denne lista finnes
 * for å avvise.
 */
export function folderAccessList(input: {
  folderId: string;
  folders: readonly FolderNode[];
  grants: readonly FolderGrantRow[];
  groups: readonly GroupWithMembers[];
  users: readonly PersonRow[];
}): FolderAccessPerson[] {
  const { folderId, folders, grants, groups, users } = input;
  const kjede = folderChain(folderId, folders);
  if (kjede.length === 0) return [];

  // En slettet mappe i kjeden blokkerer alt, som i SQL-en.
  const blokkert = kjede.some((k) => !!k.deleted_at);

  const rader: FolderAccessPerson[] = [];

  for (const u of users) {
    const mine = grantsForUser(u.id, kjede, grants, groups, folderId);

    const rangLeserAlt = kjede.every((k) =>
      hasMinRole(u.role, k.read_min_role as SystemRole),
    );
    const kanLese =
      !blokkert &&
      kjede.every(
        (k) =>
          hasMinRole(u.role, k.read_min_role as SystemRole) ||
          mine.some((g) => g.folder_id === k.id),
      );
    const kanSkrive =
      kanLese &&
      kjede.every(
        (k) =>
          hasMinRole(u.role, k.write_min_role as SystemRole) ||
          mine.some((g) => g.folder_id === k.id && g.can_write),
      );

    if (!kanLese && mine.length === 0) continue;

    rader.push({
      user_id: u.id,
      full_name: u.full_name,
      role: u.role,
      can_read: kanLese,
      can_write: kanSkrive,
      via_rank: rangLeserAlt && !blokkert,
      grants: mine,
      depends_on_group: kanLese && !rangLeserAlt,
    });
  }

  // Har tilgang først, deretter alfabetisk. De blokkerte havner nederst, der
  // UI-et viser dem som en egen advarsel.
  return rader.sort(
    (a, b) =>
      Number(b.can_read) - Number(a.can_read) ||
      a.full_name.localeCompare(b.full_name, "no"),
  );
}
